import { Link } from "react-router-dom";
import { PageHero, SectionHeading, Reveal } from "@/components/Section";
import { IMAGES } from "@/data/site";
import { ArrowRight } from "lucide-react";

const WHY_CHOOSE = [
  ["FULL RANGE ON ONE ACCOUNT","Hardware, building materials, cement, steel, roofing, tiles, mining supplies, heavy equipment. One account. One call. One delivery."],
  ["COMPETITIVE WHOLESALE PRICING","You're not paying retail. Our B2B pricing reflects long-term relationships with manufacturers and distributors."],
  ["WE'VE SUPPLIED LEADING CHAINS FOR 23 YEARS","We have been supplying leading hardware chains and contractors for 23 years. The same quality and reliability — you get the same."],
  ["DELIVERY ON YOUR TIMELINE","We confirm delivery in every quote. If we say Thursday, we mean Thursday."],
];
const CONTRACTOR_TYPES = ["General Building Contractors","Renovation Contractors","Civil Contractors","Electrical & Plumbing","Mining Contractors","Hardware Retailers & Co-ops","Facilities Management","Property Developers"];
const TOP_ORDERS = ["Cement","Steel","Roofing","Tiles","Doors & Frames","Safety PPE","Power Tools","Fasteners","Paint & Finishes","Waterproofing"];
const ACCOUNT_STEPS = [
  ["01","Complete our enquiry form or WhatsApp us"],
  ["02","We contact you within 24 hours to understand your supply needs"],
  ["03","We set up your account with agreed pricing and terms"],
  ["04","Order anytime — call, WhatsApp or email your PO"],
];
const PHOTO_STRIP = [
  { img: IMAGES.cement, t: "BUILDING MATERIALS" },
  { img: IMAGES.heavy, t: "HEAVY EQUIPMENT" },
  { img: IMAGES.forklift, t: "WAREHOUSE & LOGISTICS" },
];

const UrgencyStrip = () => (
  <section className="bg-[#E8721C] py-10" data-testid="urgency-strip">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 flex flex-wrap items-center justify-between gap-4">
      <p className="text-[#0A1628] font-bold text-[17px] md:text-[20px] tracking-wide">Set up your account today. First order gets priority processing.</p>
      <Link to="/contact" className="btn-navy" data-testid="urgency-cta">Set Up My Account</Link>
    </div>
  </section>
);

const WhyChooseSection = () => (
  <section className="bg-[#F8F6F2] py-24">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <SectionHeading label="WHY CONTRACTORS CHOOSE US" title="LESS RUNNING. MORE BUILDING." />
      <div className="grid md:grid-cols-2 gap-6">
        {WHY_CHOOSE.map(([t,d],i) => (
          <Reveal key={t} delay={i*100}>
            <div className="cg-card h-full"><h4 className="font-display text-[22px] heading-bar">{t}</h4><p className="mt-3 text-[#1E2D40] leading-7">{d}</p></div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WhoWeSupplySection = () => (
  <section className="bg-[#0A1628] text-white py-24 blueprint-grid">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <SectionHeading label="WHO WE SUPPLY" title="EVERY KIND OF CONTRACTOR." light />
      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
        {CONTRACTOR_TYPES.map((c) => (
          <div key={c} className="bg-[#1E2D40] p-5 border-l-[3px] border-[#E8721C] text-[15px]">{c}</div>
        ))}
      </div>
    </div>
  </section>
);

const TopOrdersSection = () => (
  <section className="bg-[#F8F6F2] py-24">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <SectionHeading title="WHAT CONTRACTORS ORDER MOST" />
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {TOP_ORDERS.map((p) => (
          <div key={p} className="bg-white border-l-[3px] border-[#E8721C] py-5 px-4 text-center font-display text-[18px]">{p}</div>
        ))}
      </div>
    </div>
  </section>
);

const AccountSetupSection = () => (
  <section className="bg-[#0A1628] py-24 text-white blueprint-grid">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <SectionHeading label="ACCOUNT SETUP" title="HOW TO SET UP A CONTRACTOR ACCOUNT" light />
      <div className="grid md:grid-cols-4 gap-6">
        {ACCOUNT_STEPS.map(([n,t]) => (
          <div key={n} className="cg-card-dark" style={{background:"#1E2D40"}}>
            <div className="font-display text-[42px] text-[#E8721C] leading-none">{n}</div>
            <p className="mt-4 text-[#cbd3df] leading-7">{t}</p>
          </div>
        ))}
      </div>
      <div className="mt-10"><Link to="/contact" className="btn-orange" data-testid="contractor-account-cta">Set Up Your Contractor Account <ArrowRight size={14}/></Link></div>
    </div>
  </section>
);

const PhotoStripSection = () => (
  <section className="grid grid-cols-2 md:grid-cols-3" data-testid="contractors-photo-strip">
    {PHOTO_STRIP.map((p) => (
      <div key={p.t} className="relative h-[220px] md:h-[280px] overflow-hidden group">
        <img src={p.img} alt={p.t} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/30 to-transparent"/>
        <div className="absolute bottom-5 left-5 right-5 font-display text-white text-[18px] tracking-wide border-l-[3px] border-[#E8721C] pl-3">{p.t}</div>
      </div>
    ))}
  </section>
);

const ForContractors = () => (
  <>
    <PageHero
      label="FOR CONTRACTORS & BUILDERS"
      title="ONE SUPPLIER. YOUR ENTIRE BUILD. DELIVERED."
      subtitle="You're on site. You have a schedule. You can't afford delays or running around to 6 different suppliers. We have everything — and we deliver."
      image={IMAGES.workerHelmet}
    />
    <UrgencyStrip />
    <WhyChooseSection />
    <WhoWeSupplySection />
    <TopOrdersSection />
    <AccountSetupSection />
    <PhotoStripSection />
  </>
);
export default ForContractors;
